import React, { useState, useEffect, useRef } from 'react';
import { renderNotificationMessage } from '../../utils/notificationMessage';
import { 
  Box, 
  Typography, 
  Paper, 
  Tabs, 
  Tab, 
  Divider,
  List,
  ListItem,
  ListItemText,
  Button,
  CircularProgress,
  styled
} from '@mui/material';
import { notificationService } from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useUser } from '../../context/UserContext';
import { useNotifications } from '../../context/NotificationContext';
import NominationPopup from '../Notifications/NominationPopup';
import EmptyState from '../common/EmptyState';

const NotificationItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '10px 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'flex-start'
}));

const TimeAgo = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '12px',
  marginLeft: '10px'
}));

const ApprovalItem = styled(ListItem)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
  padding: '10px 0',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}));

const ActionButtons = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: '8px'
}));

const Notifications = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [notificationData, setNotificationData] = useState({
    recent: [],
    previous: [],
    approvals: [],
    nominations: []
  });
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const { user } = useUser();
  const { 
    fetchData: fetchContextData, 
    acceptNomination, 
    rejectNomination, 
    openNominationPopup, 
    closeNominationPopup, 
    showNominationPopup, 
    currentNominationId,
    handleNotificationClick: contextHandleNotificationClick,
    // The feedback form itself is the app's (App.js), opened from here by the request's link (S-15.1 with K8 pt 5).
    showFeedbackPopup,
  } = useNotifications();

  const showPendingTab = user && ['manager'].includes(user.role_name.toLowerCase());

  const loadNotifications = async (tab) => {
    setLoading(true);
    try {
      const response = await notificationService.getNotifications(tab);
      setNotificationData(response.data);
    } catch (error) {
      console.error('Error loading notifications:', error);
      showToast('Failed to load notifications', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(activeTab);
  }, [activeTab]);

  // When the feedback form closes (submitted, or closed), the list is read again, so a submitted request leaves it.
  const feedbackWasOpen = useRef(false);
  useEffect(() => {
    if (feedbackWasOpen.current && !showFeedbackPopup) loadNotifications(activeTab);
    feedbackWasOpen.current = showFeedbackPopup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showFeedbackPopup]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleApprove = async (notificationId, courseId, learnerId) => {
    try {
      await notificationService.approve(notificationId, courseId, learnerId);
      showToast('Request approved successfully', 'success');
      loadNotifications(activeTab);
      fetchContextData(); // Refresh the notification count in the header
    } catch (error) {
      console.error('Error approving request:', error);
      showToast('Failed to approve request', 'error');
    }
  };

  const handleReject = async (notificationId, courseId, learnerId) => {
    try {
      await notificationService.reject(notificationId, courseId, learnerId);
      showToast('Request rejected', 'success');
      loadNotifications(activeTab);
      fetchContextData(); // Refresh the notification count in the header
    } catch (error) {
      console.error('Error rejecting request:', error);
      showToast('Failed to reject request', 'error');
    }
  };

  const renderNotificationItem = (notification) => (
    <NotificationItem key={notification.id}>
      <Box sx={{ width: '85%' }}>
        {/* The message as text that runs nothing, with its bold and links kept (S-26 pt 4). Removed 17 Oct 2026 (S-26 pt 4):
            inserting it as HTML, which ran whatever a learner had typed as their name. */}
        <Typography variant="body1" onClick={(e) => handleNotificationClick(notification, e)}>
          {renderNotificationMessage(notification.message, { color: 'var(--primary-color)', textDecoration: 'none' })}
        </Typography>
        {notification.actions && (
          <Box sx={{ mt: 1 }}>
            <Button 
              variant="outlined" 
              color="primary" 
              size="small" 
              sx={{ mr: 1 }}
              onClick={() => handleApprove(notification.id, notification.courseId, notification.learnerId)}
            >
              Approve
            </Button>
            <Button 
              variant="outlined" 
              color="error" 
              size="small"
              onClick={() => handleReject(notification.id, notification.courseId, notification.learnerId)}
            >
              Reject
            </Button>
          </Box>
        )}
      </Box>
      <TimeAgo>{notification.timeAgo}</TimeAgo>
    </NotificationItem>
  );

  const renderApprovalItem = (request) => (
    <ApprovalItem key={request.id}>
      <Box>
        <Typography variant="subtitle1">{request.learnerName}</Typography>
        <Typography variant="body2">Course: {request.courseName}</Typography>
      </Box>
      <ActionButtons>
        <Button 
          variant="contained" 
          color="error" 
          size="small"
          onClick={() => handleReject(request.id, request.courseId, request.learnerId)}
        >
          Reject
        </Button>
        <Button 
          variant="contained" 
          color="primary" 
          size="small"
          onClick={() => handleApprove(request.id, request.courseId, request.learnerId)}
        >
          Approve
        </Button>
      </ActionButtons>
    </ApprovalItem>
  );

  const handleNotificationClick = async (notification, e) => {   
    await contextHandleNotificationClick(notification, e);
    // Refresh the notifications list after handling the click
    await loadNotifications(activeTab);
  };

  return (
    <>
        <Box sx={{ p: 2 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>Notifications</Typography>
        <Paper sx={{ pl: 2, pr:2 }}>
            <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="All Notifications" value="all" />
            {showPendingTab && <Tab label="Pending Tasks" value="pending" />}
            </Tabs>
            <Divider sx={{ mb: 1 }} />

            {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
            ) : (
            <>
                {activeTab === 'all' && (
                <>
                    <Box sx={{ mb: 2}}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Recent (Last 7 days)</Typography>
                    <List>
                        {notificationData.recent && notificationData.recent.length > 0 ? (
                        notificationData.recent.map(renderNotificationItem)
                        ) : (
                        <ListItem sx={{ display: 'block' }}>
                            {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                            <EmptyState framed={false} title="No recent notifications" message={null} />
                        </ListItem>
                        )}
                    </List>
                    </Box>
                    <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Previous Notifications</Typography>
                    <List>
                        {notificationData.previous && notificationData.previous.length > 0 ? (
                        notificationData.previous.map(renderNotificationItem)
                        ) : (
                        <ListItem sx={{ display: 'block' }}>
                            {/* The shared empty state with this screen's own words (K2.3 pt 14). Removed 23 Oct 2026 (K2.3 pt 14, PR #8 review): a plain line. */}
                            <EmptyState framed={false} title="No previous notifications" message={null} />
                        </ListItem>
                        )}
                    </List>
                    </Box>
                </>
                )}

                {activeTab === 'pending' && (
                <>
                    <Box sx={{ mb: 2 }}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Approve Learner</Typography>
                    <List>
                        {notificationData.approvals && notificationData.approvals.length > 0 ? (
                        notificationData.approvals.map(renderApprovalItem)
                        ) : (
                        <ListItem>
                            <Typography variant="body2">No pending approvals</Typography>
                        </ListItem>
                        )}
                    </List>
                    </Box>
                    <Box>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>Nominate Learner</Typography>
                    <List>
                        {notificationData.nominations && notificationData.nominations.length > 0 ? (
                        notificationData.nominations.map(renderApprovalItem)
                        ) : (
                        <ListItem>
                            <Typography variant="body2">No pending nominations</Typography>
                        </ListItem>
                        )}
                    </List>
                    </Box>
                </>
                )}
            </>
            )}
        </Paper>
        </Box>
        <NominationPopup 
            open={showNominationPopup}
            onClose={closeNominationPopup}
            nominationId={currentNominationId}
        />

        {/* The feedback form opens once, the app's (S-15.1 with K8 pt 5). Removed 18 Oct 2026 (S-15.1): this page's own
            copy, which opened under the app's, one form over the other, with its own "Feedback submitted successfully". */}
    </>
  );
};

export default Notifications;
